import { useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useToast } from '@practium/ui';
import type { ConsultantFormData } from '../types';
import { ConsultantSchema } from '../schemas/consultant.schema';
import { CONSULTANT_FIELD_LABELS, DEFAULT_CONSULTANT_FORM } from '../constants/consultant.constants';
import { usePostBindingUser, useUpdateBindingUser } from '@practium/features/api';
import { applyPhoneMask, formatPhoneForApi, getDocumentType } from '../utils/masks';

export interface UseConsultantFormOptions {
	initialData?: ConsultantFormData;
	onSubmit: () => void;
}

export const useConsultantForm = ({ initialData, onSubmit }: UseConsultantFormOptions) => {
	const toast = useToast();
	const [searchParams] = useSearchParams();
	const routeParams = useParams<{ id?: string }>();
	const bindingUserId = searchParams.get('id') ?? routeParams.id;
	const { mutateAsync: createBindingUser } = usePostBindingUser({
		bindingType: 2,
	});
	const { mutateAsync: updateBindingUser } = useUpdateBindingUser();
	const initialForm = initialData
		? {
			...DEFAULT_CONSULTANT_FORM,
			...initialData,
			phoneNumber: applyPhoneMask(initialData.phoneNumber),
		}
		: { ...DEFAULT_CONSULTANT_FORM };
	const [form, setForm] = useState<ConsultantFormData>(initialForm);
	const initialFormRef = useRef(initialForm);
	const [isLoading, setIsLoading] = useState(false);
	const [errors, setErrors] = useState<Record<string, string>>({});

	const set = (key: keyof ConsultantFormData, value: string) =>
		setForm(f => ({ ...f, [key]: value }));

	const isDirty = () => JSON.stringify(form) !== JSON.stringify(initialFormRef.current);

	const handleSubmit = async () => {
		const result = ConsultantSchema.safeParse(form);

		if (!result.success) {
			const fieldErrors: Record<string, string> = {};
			result.error.issues.forEach(issue => {
				const path = String(issue.path[0] ?? '');
				if (path) fieldErrors[path] = issue.message;
			});
			setErrors(fieldErrors);
			const uniquePaths = [...new Set(result.error.issues.map(i => String(i.path[0] ?? '')).filter(Boolean))];
			const labels = uniquePaths.map(p => CONSULTANT_FIELD_LABELS[p] ?? p).join(', ');
			toast.error(`Campos inválidos: ${labels}`);
			return;
		}

		setErrors({});
		setIsLoading(true);
		try {
			const payload = {
				name: form.name,
				surName: form.surName,
				phoneNumber: formatPhoneForApi(form.phoneNumber) || undefined,
				email: form.email,
				document: form.document || undefined,
				documentType: getDocumentType(form.document),
				status: form.status === 'true',
				canConnect: true,
			} as const;

			if (bindingUserId) {
				await updateBindingUser({
					bindingType: 2,
					id: bindingUserId,
					data: payload,
				});
			} else {
				await createBindingUser(payload);
			}

			toast.success('Consultor salvo com sucesso!');
			onSubmit();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Erro ao salvar consultor');
		} finally {
			setIsLoading(false);
		}
	};

	return { form, errors, isLoading, set, handleSubmit, isDirty };
};
